// The rule that makes a position a puzzle, ported from v1's
// TacticPuzzleFinder. It reads a stored scan and never searches.
//
// Two kinds come out of a game, both keyed by the ply the reader was to move:
// - a `tactic`: the engine's best line was clearly ahead of its second (a
//   mate the second is not, or a gap of a pawn or more), the best line was at
//   least level, and the reader played something else. The puzzle is the
//   line to the point where its prize is settled.
// - a `mistake`: a move the scan judged a blunder, asking for the move that
//   avoids the damage. One move, the engine's.
import { uciToInput, type MoveInput } from '@chess/api-chess';
import { centipawns } from '@chess/api-analysis';
// The filters that drop pointless puzzles live in their own files:
// filter-decided.ts (the right move must change the outcome) and
// filter-no-opportunity.ts (the alternative must not already win).
import { isOutcomeChanged, MIN_SWING } from './filter-decided.ts';
import { DECISIVE, isRealOpportunity } from './filter-no-opportunity.ts';
export { MIN_SWING, DECISIVE };
export { winProbability } from './win-probability.ts';

/** Bump when the rule changes; stored lists made by an older finder are regenerated. */
export const FINDER_VERSION = 11;
/** v1's clear gap: the best line a pawn or more ahead of the second. */
export const CLEAR_GAP_CP = 100;

/** A missed forcing win, a blunder replayed, or the book left first: "keep the theory". */
export type PuzzleKind = 'tactic' | 'mistake' | 'theory';

export type Prize =
  | { kind: 'mate'; inMoves: number }
  | { kind: 'material'; won: PieceType[]; given: PieceType[]; net: number }
  | { kind: 'advantage'; centipawns: number }
  | { kind: 'avoid'; delta: number }
  /** Any book continuation is the answer; `opening` names the line the game was in. */
  | { kind: 'theory'; steps: TheoryStep[]; opening?: string };

/** One of the reader's turns in the opening puzzle: the position and the moves accepted there. */
export interface TheoryStep {
  /** The position the reader is to move in; the game's move from it is `moves[ply]`. */
  ply: number;
  answers: San[];
  /** The book's moves, or the engine's best once the theory has run out. */
  source: 'book' | 'engine';
}

export interface Puzzle {
  /** `${kind}:${ply}`, stable across regenerations. */
  id: string;
  kind: PuzzleKind;
  /** The position the reader was to move in; the game's move from it is `moves[ply]`. */
  ply: number;
  side: Color;
  fen: Fen;
  /** The moves to play, reader first, alternating; replies are the engine's line. */
  line: Uci[];
  prize: Prize;
  /** What the reader played instead. */
  played: Uci;
  /** Centipawns the reader's move gave up against the best line at this ply: the score variation. */
  swing: number;
  /** The position was still in the opening book, so the move before it was part of an opening. */
  inOpening?: boolean;
}

/** What the finder needs from the rules of chess: to play a move and see what it captured. */
export interface FinderRules {
  play(fen: Fen, move: MoveInput): { fen: Fen; captured?: PieceType; checkmate: boolean } | undefined;
}

export interface FinderGame {
  moves: { uci: Uci }[];
  /** `positions[i]` is before `moves[i]`. */
  positions: Fen[];
}

// `balance`, a side's material lead in pawns, is the review API's (material.ts) since 2026-10-14; re-exported for this plugin's readers.
export { balance };

function sideToMove(fen: Fen): Color {
  return fen.split(' ')[1] === 'b' ? 'black' : 'white';
}

/** v1's rule: a mate the second line is not, or a non-losing score a clear gap ahead. */
export function isClearlyAhead(best: Line, second: Line, gap = CLEAR_GAP_CP): boolean {
  if (best.score.mate !== undefined) {
    if (best.score.mate <= 0) return false;
    if (second.score.mate !== undefined && second.score.mate > 0) return false;
    return true;
  }
  const score = best.score.cp ?? 0;
  if (score < 0) return false;
  if (second.score.mate !== undefined) return second.score.mate < 0;
  return score - (second.score.cp ?? 0) >= gap;
}

interface Played {
  uci: Uci;
  fen: Fen;
  captured?: PieceType;
  checkmate: boolean;
}

/** Plays a stored line as far as it is legal. */
function resolve(line: Uci[], fen: Fen, rules: FinderRules): Played[] {
  const out: Played[] = [];
  let current = fen;
  for (const uci of line) {
    const played = rules.play(current, uciToInput(uci));
    if (!played) break;
    out.push({ uci, fen: played.fen, captured: played.captured, checkmate: played.checkmate });
    current = played.fen;
  }
  return out;
}

/** What the line wins and where to cut it, as v1 decided it. */
function prizeOf(played: Played[], fen: Fen, best: Line): { line: Uci[]; prize: Prize } {
  const reader = sideToMove(fen);
  const opening = balance(fen, reader);
  const nets = played.map((p) => balance(p.fen, reader) - opening);
  if (best.score.mate !== undefined && best.score.mate > 0) {
    // To the mate, or to the reader's last move short of it.
    const last = played[played.length - 1];
    const count = last.checkmate || played.length % 2 === 1 ? played.length : played.length - 1;
    return { line: played.slice(0, Math.max(count, 1)).map((p) => p.uci), prize: { kind: 'mate', inMoves: best.score.mate } };
  }
  const final = nets[nets.length - 1];
  if (final > 0) {
    // The first of the reader's moves after which the count is settled, or
    // the whole line when the other side's last reply is what settles it.
    let cut = played.length;
    for (let length = 1; length <= played.length; length += 2) {
      if (nets.slice(length - 1).every((n) => n === final)) {
        cut = length;
        break;
      }
    }
    const line = played.slice(0, cut);
    const won: PieceType[] = [];
    const given: PieceType[] = [];
    line.forEach((p, i) => {
      if (!p.captured) return;
      (i % 2 === 0 ? won : given).push(p.captured);
    });
    for (const kind of [...given]) {
      const w = won.indexOf(kind);
      const g = given.indexOf(kind);
      if (w >= 0 && g >= 0) {
        won.splice(w, 1);
        given.splice(g, 1);
      }
    }
    return { line: line.map((p) => p.uci), prize: { kind: 'material', won, given, net: final } };
  }
  return { line: [played[0].uci], prize: { kind: 'advantage', centipawns: best.score.cp ?? 0 } };
}

/** The tactic a position holds, if the reader missed one. */
export function findTactic(position: PositionAnalysis, playedUci: Uci, rules: FinderRules): Omit<Puzzle, 'id' | 'kind' | 'side' | 'swing'> | undefined {
  const [best, second] = position.lines;
  if (!best || !second) return undefined;
  if (playedUci === best.uci) return undefined;
  if (!isClearlyAhead(best, second)) return undefined;
  const played = resolve(best.pv.length > 0 ? best.pv : [best.uci], position.fen, rules);
  if (played.length === 0) return undefined;
  const { line, prize } = prizeOf(played, position.fen, best);
  return { ply: position.ply, fen: position.fen, line, prize, played: playedUci };
}

/**
 * Whether a position is worth a puzzle: the right move must matter. `best` is
 * the best line's score and `played` the score after the reader's move, both
 * in centipawns from the reader's side; `second`, when given, is the
 * second-best line's score, which must not already win decisively.
 */
export function isWorthPosing(best: number, played: number, second?: number): boolean {
  return isOutcomeChanged(best, played) && isRealOpportunity(second);
}

/** The score after the reader's move, from the reader's side, out of the scan. */
function playedScore(scan: Scan, ply: number, best: number, judgement: MoveJudgement | undefined): number {
  const after = scan.positions.find((p) => p.ply === ply + 1);
  if (after) return -centipawns(after.score);
  return judgement ? best - judgement.delta : best;
}

/** Every puzzle in a complete scan for the reader's side, in ply order. */
export function findPuzzles(scan: Scan, game: FinderGame, side: Color | undefined, rules: FinderRules): Puzzle[] {
  const puzzles: Puzzle[] = [];
  // No reader side, no puzzles: a game of somebody else teaches the reader nothing here.
  if (!side) return puzzles;
  for (let ply = 0; ply < game.moves.length; ply++) {
    const fen = game.positions[ply];
    if (!fen || sideToMove(fen) !== side) continue;
    const position = scan.positions.find((p) => p.ply === ply);
    const playedUci = game.moves[ply].uci;
    const judgement: MoveJudgement | undefined = scan.moves.find((m) => m.ply === ply + 1);
    if (position) {
      const best = centipawns(position.lines[0]?.score ?? position.score);
      const second = position.lines[1] ? centipawns(position.lines[1].score) : undefined;
      if (!isWorthPosing(best, playedScore(scan, ply, best, judgement), second)) continue;
    }
    const tactic = position ? findTactic(position, playedUci, rules) : undefined;
    const swing = judgement?.delta ?? 0;
    if (tactic) {
      // A missed mate outranks any centipawn amount, whatever the judgement's delta says.
      const mateSwing = tactic.prize.kind === 'mate' ? 10000 - tactic.prize.inMoves : 0;
      puzzles.push({ id: `tactic:${ply}`, kind: 'tactic', side, ...tactic, swing: Math.max(swing, mateSwing) });
      continue;
    }
    // A blunder by the app's one rule (`isBlunderUnder`), at the default line: the list is stored, not redone when the settings change.
    if (judgement && isBlunderUnder(judgement) && position) {
      puzzles.push({
        id: `mistake:${ply}`,
        kind: 'mistake',
        ply,
        side,
        fen,
        line: [judgement.best],
        prize: { kind: 'avoid', delta: judgement.delta },
        played: playedUci,
        swing,
      });
    }
  }
  return onePuzzlePerMate(puzzles);
}

/**
 * The puzzle for one of the reader's moves, made on demand for the stage's
 * blunders-and-misses table: the missed tactic when the position holds one,
 * else "find the better move" with the engine's move as the answer. Unlike
 * `findPuzzles` it applies no worth-posing filter, so every row can be
 * practised. `ply` is the position the move was played from; the ids match
 * the stored list's, so attempts are shared.
 */
export function puzzleForMove(scan: Scan, game: FinderGame, ply: number, side: Color, rules: FinderRules): Puzzle | undefined {
  const fen = game.positions[ply];
  const played = game.moves[ply]?.uci;
  const position = scan.positions.find((p) => p.ply === ply);
  if (!fen || !played || !position) return undefined;
  const judgement: MoveJudgement | undefined = scan.moves.find((m) => m.ply === ply + 1);
  const swing = judgement?.delta ?? 0;
  const tactic = findTactic(position, played, rules);
  if (tactic) {
    const mateSwing = tactic.prize.kind === 'mate' ? 10000 - tactic.prize.inMoves : 0;
    return { id: `tactic:${ply}`, kind: 'tactic', side, ...tactic, swing: Math.max(swing, mateSwing) };
  }
  const best = judgement?.best ?? position.lines[0]?.uci;
  if (!best || best === played) return undefined;
  return { id: `mistake:${ply}`, kind: 'mistake', ply, side, fen, line: [best], prize: { kind: 'avoid', delta: swing }, played, swing };
}

/**
 * The pieces on the board regardless of their squares: the sorted piece
 * letters of the FEN's board field. Two positions with the same signature
 * have the same material on both sides, so a mate missed in one and again in
 * the other is the same mate found late.
 */
export function pieceSignature(fen: Fen): string {
  return fen
    .split(' ')[0]
    .replace(/[^a-zA-Z]/g, '')
    .split('')
    .sort()
    .join('');
}

/**
 * A reader who misses a mate often keeps missing it over the next moves with
 * the same pieces on the board, and the scan then yields a mate puzzle at
 * every one of those plies. Consecutive mate puzzles with the same piece
 * signature are one group, and only one of them is shown: the shortest
 * mate, and the earliest ply when the lengths tie. A capture or promotion
 * changes the signature and a puzzle of another kind in between breaks the
 * run, either of which starts a new group. The opponent's intervening moves
 * do not. Puzzles that are not missed mates are untouched. The list must be
 * in ply order, as `findPuzzles` makes it.
 */
export function onePuzzlePerMate(puzzles: Puzzle[]): Puzzle[] {
  const isMate = (p: Puzzle) => p.kind === 'tactic' && p.prize.kind === 'mate';
  const mateMoves = (p: Puzzle) => (p.prize.kind === 'mate' ? p.prize.inMoves : Infinity);
  const out: Puzzle[] = [];
  let group: { signature: string; best: Puzzle } | undefined;
  const flush = () => {
    if (group) out.push(group.best);
    group = undefined;
  };
  for (const puzzle of puzzles) {
    if (!isMate(puzzle)) {
      flush();
      out.push(puzzle);
      continue;
    }
    const signature = pieceSignature(puzzle.fen);
    if (group && group.signature === signature) {
      if (mateMoves(puzzle) < mateMoves(group.best)) group.best = puzzle;
      continue;
    }
    flush();
    group = { signature, best: puzzle };
  }
  flush();
  return out;
}

const PIECE_NAME: Record<PieceType, string> = { p: 'a pawn', n: 'a knight', b: 'a bishop', r: 'a rook', q: 'a queen', k: 'the king' };

/**
 * Puzzles from the biggest score variation down, equal swings in game order,
 * with the mate puzzles after all the others: a missed mate is the easiest
 * kind to spot once told, so the list leads with the positional lessons.
 */
export function sortBySwing(puzzles: Puzzle[]): Puzzle[] {
  const isMate = (p: Puzzle) => p.prize.kind === 'mate';
  // The opening's own puzzle leads its list: it is the question every opening asks.
  const isTheory = (p: Puzzle) => p.kind === 'theory';
  return [...puzzles].sort(
    (a, b) => Number(isTheory(b)) - Number(isTheory(a)) || Number(isMate(a)) - Number(isMate(b)) || b.swing - a.swing || a.ply - b.ply,
  );
}

/**
 * What happened in the row. Inside the opening book a missed tactic means
 * the opponent left theory with a blunder the reader did not punish, and a
 * blunder is an opening mistake; a theory puzzle is the reader leaving the
 * book first.
 */
export function describeKind(kind: PuzzleKind, inOpening = false): string {
  if (kind === 'theory') return 'Complete the opening';
  if (inOpening) return kind === 'tactic' ? 'Punish the deviation' : 'Opening mistake';
  return kind === 'tactic' ? 'Missed opportunity' : 'Lost score';
}

/** What the opening puzzle draws its answers from. */
export interface TheorySources {
  continuations(fen: Fen): Array<{ san: San }>;
  /** The engine's best move from the position at `ply`, in SAN, when the scan has it. */
  bestSan(ply: number): San | undefined;
  /** Centipawns the reader's move at `ply` gave up, when judged. */
  swingAt(ply: number): number;
}

/**
 * The opening's one puzzle, "Complete the opening": the reader replays their
 * own moves of the opening from move 1, any book continuation accepted at
 * each of their turns, with the opponent's moves as the game had them. The
 * puzzle ends where the game left the book; when it was the reader who left,
 * the last step asks instead for the engine's best move there, so the
 * opening is completed with the best move. Undefined when the reader had no
 * move inside the book.
 */
export function findTheory(
  game: FinderGame,
  reading: { lastKnownPly: number; opening?: { name: string } } | undefined,
  side: Color | undefined,
  sources: TheorySources,
): Puzzle | undefined {
  if (!reading || !side) return undefined;
  const exitPly = reading.lastKnownPly; // the move from here left the book, when one followed
  const left = exitPly < game.moves.length;
  const readerLeft = left && !!game.positions[exitPly] && sideToMove(game.positions[exitPly]) === side;
  const last = Math.min(exitPly, game.moves.length - 1);
  const steps: TheoryStep[] = [];
  for (let ply = 0; ply <= last; ply++) {
    const fen = game.positions[ply];
    if (!fen || sideToMove(fen) !== side) continue;
    const book = sources.continuations(fen).map((c) => c.san);
    if (readerLeft && ply === exitPly) {
      // The reader's own move left the book: the last step asks for the engine's best move
      // from here, with any book move still accepted as a way to complete the opening.
      const best = sources.bestSan(ply);
      const answers = best ? [best, ...book.filter((san) => san !== best)] : book;
      if (answers.length > 0) steps.push({ ply, answers, source: best ? 'engine' : 'book' });
      break;
    }
    if (book.length === 0) break;
    steps.push({ ply, answers: book, source: 'book' });
  }
  if (steps.length === 0 || !steps.some((s) => s.source === 'book')) return undefined;
  // The puzzle starts at the last move of the theory: the reader's final turn in the book,
  // or, when the reader's own move left it, the position the book ends at. Earlier book
  // moves are already on the board and are not asked again.
  const first = steps[steps.length - 1];
  // The score column is what the reader's first move after the book gave up: the leaving
  // move when the reader left, their reply when the opponent did.
  const readerExit = readerLeft ? exitPly : exitPly + 1;
  return {
    id: `theory:${first.ply}`,
    kind: 'theory',
    ply: first.ply,
    side,
    fen: game.positions[first.ply],
    line: [],
    prize: { kind: 'theory', steps: [first], opening: reading.opening?.name },
    played: game.moves[first.ply].uci,
    swing: left && readerExit < game.moves.length ? sources.swingAt(readerExit) : 0,
    inOpening: true,
  };
}

/**
 * The puzzles a stage shows: the opening has only its own puzzle, replaying
 * the theory; the other stages have the engine's puzzles that fell inside
 * them.
 */
export function puzzlesForPhase(puzzles: Puzzle[], phase: 'opening' | 'middlegame' | 'ending', fromPly: number, toPly: number): Puzzle[] {
  if (phase === 'opening') return puzzles.filter((p) => p.kind === 'theory');
  return puzzlesInRange(puzzles, fromPly, toPly).filter((p) => p.kind !== 'theory');
}

/**
 * The score involved, in the shortlist's and score bar's conventions: a missed
 * opportunity is the gain not taken, "+3.2 missed" or "M3 missed"; a lost
 * score is what the move gave up, "\u22122.1 lost" or "Mate lost".
 */
export function describeSwing(puzzle: Pick<Puzzle, 'kind' | 'prize' | 'swing'>): string {
  const pawns = (puzzle.swing / 100).toFixed(1);
  if (puzzle.kind === 'theory') return puzzle.swing >= 5000 ? 'Mate lost' : puzzle.swing > 0 ? `−${pawns} lost` : 'left the book';
  if (puzzle.kind === 'tactic') {
    if (puzzle.prize.kind === 'mate') return `M${puzzle.prize.inMoves} missed`;
    return `+${pawns} missed`;
  }
  if (puzzle.swing >= 5000) return 'Mate lost';
  return `\u2212${pawns} lost`;
}

/** The prize in words, as v1 said it: "Mate in three", "A knight", "The exchange". */
export function describePrize(prize: Prize): string {
  switch (prize.kind) {
    case 'mate':
      return `Mate in ${prize.inMoves}`;
    case 'material': {
      if (prize.won.length === 1 && prize.given.length === 0) return capitalize(PIECE_NAME[prize.won[0]]);
      const minor = (k: PieceType) => k === 'n' || k === 'b';
      if (prize.won.length === 1 && prize.given.length === 1 && prize.won[0] === 'r' && minor(prize.given[0])) return 'The exchange';
      if (prize.won.length === 0) return `Material worth ${prize.net}`;
      return capitalize(prize.won.map((k) => PIECE_NAME[k]).join(' and ')) + (prize.given.length ? ` for ${prize.given.map((k) => PIECE_NAME[k]).join(' and ')}` : '');
    }
    case 'advantage':
      return `A winning advantage (+${(prize.centipawns / 100).toFixed(1)})`;
    case 'avoid':
      if (prize.delta >= 5000) return 'Walked into a mate';
      return `Lost ${(prize.delta / 100).toFixed(1)} pawns`;
    case 'theory': {
      const what = prize.opening ? `the ${prize.opening}` : 'the theory';
      return prize.steps.some((s) => s.source === 'engine') ? `The best move out of ${what}` : `The last move of ${what}`;
    }
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The puzzle's move label, "23." or "31...". */
export function plyLabel(ply: number): string {
  return `${Math.floor(ply / 2) + 1}${ply % 2 === 0 ? '.' : '...'}`;
}

/** Centipawns helper re-exported for callers that describe scores. */
export { centipawns };

/** Which side is the reader's: the review API's one rule, under this plugin's name. */
import { balance, isBlunderUnder, readerSideOf } from '@chess/api-review';
import { type Color, type Fen, type PieceType, type San, type Uci, type Line, type MoveJudgement, type PositionAnalysis, type Scan } from '@chess/api-model';
export const readerSide = readerSideOf;

/**
 * Flags the puzzles whose position the opening book still knows: the move
 * before each was part of a named opening line. Without a book nothing is
 * flagged, so a build without the openings provider keeps the plain labels.
 */
export function markInOpening(puzzles: Puzzle[], book: { knows(fen: Fen): boolean } | undefined): Puzzle[] {
  if (book) for (const p of puzzles) p.inOpening = book.knows(p.fen);
  return puzzles;
}

/**
 * The puzzles that fell inside a phase: a puzzle's `ply` is the position
 * the reader was to move in, and the move from it belongs to the phase
 * whose positions run from `fromPly` to `toPly`.
 */
export function puzzlesInRange(puzzles: Puzzle[], fromPly: number, toPly: number): Puzzle[] {
  return puzzles.filter((p) => p.ply >= fromPly && p.ply < toPly);
}

